import { z } from "zod";
import { apiErrorSchema } from "@/lib/schemas/common";

export class ApiError extends Error {
  constructor(public code: string, message: string, public fields: Record<string, string[]> = {}, public status = 400, public meta: Record<string, unknown> = {}) {
    super(message);
  }
}

let csrfToken = "";

const technicalErrorPattern = /(?:<!doctype|<html|unexpected token|not valid json|traceback|django|serializer|endpoint|integrityerror|operationalerror|\[object object\]|\bundefined\b|\bnull\b)/i;

export function userFacingApiMessage(message: string, status = 400) {
  const normalized = message.replace(/\s+/g, " ").trim();

  if (/csrf/i.test(normalized)) {
    return "Oturum güvenliği yenilenmeli. Sayfayı yenileyip işlemi tekrar deneyin.";
  }
  if (status >= 500) {
    return "Sunucu tarafında geçici bir sorun oluştu. Lütfen kısa bir süre sonra tekrar deneyin.";
  }
  if (!normalized || normalized.length > 500 || technicalErrorPattern.test(normalized)) {
    return "İşlem tamamlanamadı. Sayfayı yenileyip tekrar deneyin.";
  }
  return normalized;
}

async function ensureCsrf() {
  if (csrfToken) return csrfToken;
  const response = await fetch("/api/admin/v2/auth/csrf/", { credentials: "include", cache: "no-store" });
  const value = await response.json();
  csrfToken = value.data?.csrf_token ?? "";
  return csrfToken;
}

export async function apiRequest<T extends z.ZodTypeAny>(
  path: string,
  schema: T,
  init: RequestInit = {}
): Promise<{ data: z.infer<T>; meta: Record<string, unknown> }> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);
  if (!(init.body instanceof FormData) && init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    headers.set("X-CSRFToken", await ensureCsrf());
    if (!headers.has("Idempotency-Key")) headers.set("Idempotency-Key", crypto.randomUUID());
  }
  const requestUrl = `/api/admin/v2/${path.replace(/^\//, "")}`;
  const requestInit = {
    ...init, method, headers, credentials: "include", cache: "no-store"
  } satisfies RequestInit;
  let response = await fetch(requestUrl, requestInit);
  if (
    response.status === 403
    && !["GET", "HEAD", "OPTIONS"].includes(method)
    && !response.headers.get("Content-Type")?.includes("application/json")
  ) {
    resetCsrf();
    headers.set("X-CSRFToken", await ensureCsrf());
    response = await fetch(requestUrl, requestInit);
  }
  if (response.status === 204) {
    const parsed = schema.safeParse(undefined);
    if (!parsed.success) throw new ApiError("invalid_response", "Sunucu yanıtı beklenen biçimde değil.");
    return { data: parsed.data as z.infer<T>, meta: {} };
  }
  const responseText = await response.text();
  let payload: Record<string, unknown>;
  try {
    const parsed = JSON.parse(responseText) as unknown;
    payload = parsed && typeof parsed === "object" ? parsed as Record<string, unknown> : {};
  } catch {
    throw new ApiError(
      "invalid_response",
      response.status === 403 ? "Oturum güvenliği yenilendi. Lütfen işlemi tekrar deneyin." : "Sunucu beklenen yanıtı vermedi.",
      {},
      response.status,
    );
  }
  if (!response.ok || payload.ok === false) {
    const parsed = apiErrorSchema.safeParse(payload);
    if (parsed.success) throw new ApiError(
      parsed.data.error.code,
      userFacingApiMessage(parsed.data.error.message, response.status),
      parsed.data.error.fields,
      response.status,
      parsed.data.meta,
    );
    throw new ApiError("request_failed", "İşlem tamamlanamadı.", {}, response.status);
  }
  if (payload?.ok !== true) throw new ApiError("invalid_response", "Sunucu yanıtı beklenen biçimde değil.");
  const parsed = schema.safeParse(payload.data);
  if (!parsed.success) throw new ApiError("invalid_response", "Sunucu yanıtı beklenen biçimde değil.");
  const meta = payload.meta && typeof payload.meta === "object" ? payload.meta as Record<string, unknown> : {};
  return { data: parsed.data as z.infer<T>, meta };
}

export function jsonBody(value: unknown): RequestInit {
  return { body: JSON.stringify(value) };
}

export function resetCsrf() { csrfToken = ""; }

export async function protectedDownload(path: string) {
  const response = await fetch(`/api/admin/v2/${path.replace(/^\//, "")}`, {
    credentials: "include",
    cache: "no-store",
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    const parsed = apiErrorSchema.safeParse(payload);
    if (parsed.success) throw new ApiError(
      parsed.data.error.code,
      userFacingApiMessage(parsed.data.error.message, response.status),
      parsed.data.error.fields,
      response.status,
      parsed.data.meta,
    );
    throw new ApiError("download_failed", "Dosya indirilemedi.", {}, response.status);
  }
  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const match = disposition.match(/filename="?([^";]+)"?/i);
  const name = match?.[1] ?? "dosya";
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
